import React, { useEffect } from 'react';
import AnimatedSection from '../components/AnimatedSection';
import { trackPageView } from '../utils/analytics';

const ReturnPolicy = () => {
  useEffect(() => {
    trackPageView('/return-policy', 'Return Policy');
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A1F44] to-[#0056D2] py-16">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Return & Refund Policy
            </h1>
            <p className="text-lg text-blue-100">
              Last Updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="prose prose-lg max-w-none space-y-8">
              <div className="bg-[#F5F7FA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">1. 7-Day Money-Back Guarantee</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We offer a 7-day money-back guarantee on all our plans. If you're not satisfied with our service for any reason, you can request a full refund within 7 days of your initial purchase.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Conditions:</strong>
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
                  <li className="font-semibold text-[#0056D2]">Customer can request refund only within 7 days of starting the subscription (for all plans: Monthly/Annually)</li>
                  <li>Applies to first-time subscriptions only</li>
                  <li>Setup fees (if any) are non-refundable</li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 border-2 border-gray-100">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">2. Cancellation Policy</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You can cancel your subscription at any time through your account dashboard or by contacting our support team.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Monthly Plans:</strong> Cancellation takes effect at the end of the current billing cycle. No refund for partial months.</li>
                  <li><strong>Annual Plans:</strong> Service continues until subscription end date.</li>
                </ul>
              </div>

              <div className="bg-[#F5F7FA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">3. Refund Processing Time</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Once your refund request is approved:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Processing time: 2-3 business days</li>
                  <li>Bank account refunds: 7-10 business days</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  You will receive an email confirmation once the refund has been processed.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 border-2 border-gray-100">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">4. Non-Refundable Items</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The following are non-refundable:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>One-time setup fees</li>
                  <li>Custom development work</li>
                  <li>Domain registration fees</li>
                  <li>Third-party software licenses</li>
                  <li>Add-on services beyond the subscription</li>
                </ul>
              </div>

              <div className="bg-[#F5F7FA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">5. How to Request a Refund</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  To request a refund, please follow these steps:
                </p>
                <ol className="list-decimal list-inside text-gray-700 space-y-3 ml-4">
                  <li>Contact our support team via email at <strong>info@tallybharat.com</strong> or call <strong>+91 9829055959</strong></li>
                  <li>Provide your account details and order number</li>
                  <li>State the reason for your refund request</li>
                  <li>Our team will review your request within 24 hours</li>
                  <li>Once approved, refund will be initiated immediately</li>
                </ol>
              </div>

              <div className="bg-white rounded-2xl p-8 border-2 border-gray-100">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">6. Service Disruption Refunds</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If our service experiences downtime beyond our 99.5% uptime SLA:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>You may be eligible for service credits</li>
                  <li>Credits are calculated based on the duration of the disruption</li>
                  <li>Credits will be applied to your next billing cycle</li>
                  <li>Claims must be submitted within 7 days of the incident</li>
                </ul>
              </div>

              <div className="bg-[#F5F7FA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">7. Escalation Process</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you're not satisfied with the resolution:
                </p>
                <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                  <li>Contact our support team (response within 24 hours)</li>
                  <li>Request escalation to a senior manager (response within 48 hours)</li>
                  <li>Final escalation to management team (response within 72 hours)</li>
                </ol>
              </div>

              <div className="bg-gradient-to-r from-[#0056D2] to-[#0A1F44] rounded-2xl p-8 text-white">
                <h2 className="text-2xl font-bold mb-4">8. Contact Information</h2>
                <p className="leading-relaxed mb-4">
                  For any questions regarding our Return & Refund Policy:
                </p>
                <ul className="space-y-2">
                  <li><strong>Email:</strong> info@tallybharat.com</li>
                  <li><strong>Phone:</strong> +91 9829055959, +91 8094300200</li>
                  <li><strong>Support Hours:</strong> Mon–Sat, 10 AM – 7 PM</li>
                  <li><strong>Address:</strong> Block H, Boranada Industrial Area, Jodhpur - 342001</li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 border-2 border-[#FF7A00]">
                <h2 className="text-2xl font-bold text-[#0A1F44] mb-4">Customer Satisfaction Guarantee</h2>
                <p className="text-gray-700 leading-relaxed">
                  At TallyBharat, your satisfaction is our priority. We're committed to providing excellent service and support. If you encounter any issues, please reach out to us immediately. We're here to help and will do everything we can to ensure you're happy with our service.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default ReturnPolicy;